import Phaser from "phaser";
import { gameRenderScale } from "./renderScale";
import { MARKET_ART } from "../config/marketArt";

export type MarketActivity = { setState(state: "working" | "done" | "paused"): void; destroy(): void };
/** Reuses the approved Phaser balance-scale art; only small facets travel into its bowls. */
export function createMarketActivity(parent: HTMLElement, reducedMotion: boolean): MarketActivity {
  let state: "working" | "done" | "paused" = "working";
  const ratio = gameRenderScale(window.devicePixelRatio || 1, document.documentElement.dataset.platform);
  reducedMotion ||= window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  class Activity extends Phaser.Scene {
    art?: Phaser.GameObjects.Image;
    facets: Phaser.GameObjects.Graphics[] = [];
    preload() { this.load.image("market-scales", MARKET_ART.exchange); }
    create() {
      this.art = this.add.image(0, 0, "market-scales");
      for (let i = 0; i < 4; i++) {
        const gem = this.add.graphics().fillStyle(i % 2 ? 0xb9a2ed : 0xf6d394, 1);
        gem.fillPoints([{ x: 0, y: -4 }, { x: 3, y: 0 }, { x: 0, y: 5 }, { x: -3, y: 0 }], true);
        this.facets.push(gem);
      }
      this.layout(); this.scale.on("resize", () => this.layout());
      this.game.events.once(Phaser.Core.Events.POST_RENDER, () => { parent.dataset.activityReady = "true"; });
    }
    layout() {
      const w = this.scale.width, h = this.scale.height, size = Math.min(w * .92, h * .89);
      this.art?.setPosition(w * .5, h * .56).setDisplaySize(size, size);
    }
    override update(time: number) {
      this.facets.forEach((gem, i) => {
        const phase = (time / 1800 + i / 2) % 1, right = i % 2;
        const moving = state === "working" && !reducedMotion;
        gem.setVisible(moving).setScale(ratio * .9)
          .setPosition(this.scale.width * (right ? .77 : .24), this.scale.height * (.08 + phase * .44))
          .setAlpha(moving ? Math.sin(phase * Math.PI) * .95 : 0);
      });
    }
  }
  const scene = new Activity({ key: "MarketActivity" });
  const canvas = document.createElement("canvas"); canvas.setAttribute("aria-hidden", "true");
  const game = new Phaser.Game({ type: Phaser.CANVAS, canvas, parent, transparent: true, banner: false,
    width: Math.max(1, parent.clientWidth * ratio), height: Math.max(1, parent.clientHeight * ratio),
    audio: { noAudio: true }, fps: { target: reducedMotion ? 10 : 30 }, scene: [scene] });
  const resize = new ResizeObserver(() => game.scale.resize(Math.max(1, parent.clientWidth * ratio), Math.max(1, parent.clientHeight * ratio)));
  resize.observe(parent);
  const visibility = () => {
    if (document.hidden && scene.sys.isActive()) scene.scene.pause();
    else if (!document.hidden && scene.sys.isPaused()) scene.scene.resume();
  };
  document.addEventListener("visibilitychange", visibility);
  return { setState(next) { state = next; }, destroy() { resize.disconnect(); document.removeEventListener("visibilitychange", visibility); game.destroy(true); canvas.remove(); delete parent.dataset.activityReady; } };
}
